<template>
  <article class="discussed-asset-banner">
    <div class="discussed-asset-banner__head">
      <div class="discussed-asset-banner__label">{{ eyebrow }}</div>
      <div
        v-if="offer"
        class="discussed-asset-banner__offer"
        :class="`discussed-asset-banner__offer--${offerTone}`"
      >{{ offer.label }}<template v-if="offer.amount != null"> · <span class="discussed-asset-banner__offer-amount">{{ priceLabel(offer.amount) }}</span></template></div>
    </div>
    <div class="discussed-asset-banner__row">
      <div class="discussed-asset-banner__media">
        <img
          v-if="asset.image"
          :src="asset.image"
          :alt="assetTitle(asset)"
        >
        <div v-else class="discussed-asset-banner__media-fallback" />
      </div>
      <div class="discussed-asset-banner__body">
        <div class="discussed-asset-banner__title">{{ assetTitle(asset) }}</div>
        <!-- The figures an offer is weighed against, where the consumer has
             them; the price and the countdown where it has not. -->
        <div v-if="hasFigures" class="discussed-asset-banner__figures">
          <template v-if="asset.ask != null">{{ askLabel }} <span class="discussed-asset-banner__figure">{{ priceLabel(asset.ask) }}</span></template>
          <template v-if="asset.ask != null && asset.mmr != null"> · </template>
          <template v-if="asset.mmr != null">MMR <span class="discussed-asset-banner__figure discussed-asset-banner__figure--mmr">{{ priceLabel(asset.mmr) }}</span></template>
        </div>
        <div v-else class="discussed-asset-banner__meta">
          <span v-if="asset.price != null">{{ priceLabel(asset.price) }}</span>
          <template v-if="auctionLabel">
            <span v-if="asset.price != null" class="discussed-asset-banner__dot">·</span>
            <span>{{ auctionsInLine }}</span>
          </template>
        </div>
      </div>
      <button v-if="!offer" class="discussed-asset-banner__view" @click="$emit('open-asset', asset)">{{ viewLabel }}</button>
    </div>
    <!-- With an offer the card's one action is the offer, as its footer. -->
    <button
      v-if="offer"
      type="button"
      class="discussed-asset-banner__footer"
      @click="$emit('view-offer', asset)"
    >
      {{ viewOfferLabel }}
      <i class="pi pi-arrow-right discussed-asset-banner__footer-icon" aria-hidden="true" />
    </button>
  </article>
</template>

<script>
// The tone each offer state takes: OfferCard's pill, so an offer reads the same
// above its thread as it does in My Offers.
const OFFER_TONES = Object.freeze({
  pending: 'info',
  countered: 'warning',
  accepted: 'success',
  declined: 'danger',
  withdrawn: 'neutral',
  expired: 'neutral',
});
</script>

<script setup>
import { computed } from 'vue';
import { formatPrice } from '../../utils/formatters';

defineOptions({ name: 'DiscussedAssetBanner' });
const props = defineProps({
  // { title?, year?, make?, model?, image?, price?, ask?, mmr? }. `ask` and
  // `mmr` are whole dollars; either one puts the Ask / MMR line where the
  // price line would be.
  asset: { type: Object, required: true },
  auctionLabel: { type: String, default: null },
  // The offer on this asset, or null for none:
  //   { state, label, amount }
  //   state  — 'pending' | 'countered' | 'accepted' | 'declined' | 'withdrawn'
  //            | 'expired', which picks the tone.
  //   label  — that state in words, in the reader's language.
  //   amount — the whole dollars that state is about, or null.
  offer: {
    type: Object,
    default: null,
    validator: (value) => Object.hasOwn(OFFER_TONES, value?.state ?? '') && typeof value.label === 'string',
  },
  // The card's words, in the reader's language.
  eyebrow: { type: String, default: 'Discussing' },
  askLabel: { type: String, default: 'Ask' },
  viewOfferLabel: { type: String, default: 'View offer' },
  // The button that opens the asset when there is no offer to open instead.
  viewLabel: { type: String, default: 'View' },
  // The whole sentence naming when the asset auctions, `{time}` standing for
  // `auctionLabel`. A caller that passes nothing gets the English the card has
  // always drawn, so no other consumer and no story moves.
  auctionsInLabel: { type: String, default: 'Auctions in {time}' },
});

defineEmits(['open-asset', 'view-offer']);

const offerTone = computed(() => OFFER_TONES[props.offer?.state] ?? 'neutral');

// Where the countdown goes inside the sentence is the translation's decision,
// so the sentence carries the placeholder rather than a prefix and a value.
const auctionsInLine = computed(() => props.auctionsInLabel.replace('{time}', props.auctionLabel ?? ''));
const hasFigures = computed(() => props.asset.ask != null || props.asset.mmr != null);

const assetTitle = (v) =>
  v?.title || [v?.year, v?.make, v?.model].filter(Boolean).join(' ');
const priceLabel = (n) => formatPrice(n, '');
</script>

<style scoped>
/* ── The card ──────────────────────────────────────────────────────── */
.discussed-asset-banner {
  overflow: hidden;
  background: var(--p-discussed-asset-banner-background);
  border: var(--p-discussed-asset-banner-border-width) solid var(--p-discussed-asset-banner-border-color);
  border-radius: var(--p-discussed-asset-banner-border-radius);
}

.discussed-asset-banner__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--p-discussed-asset-banner-head-gap);
  padding: var(--p-discussed-asset-banner-head-padding);
}

.discussed-asset-banner__label {
  font-size: var(--p-discussed-asset-banner-label-font-size);
  letter-spacing: var(--p-discussed-asset-banner-label-letter-spacing);
  font-weight: var(--p-discussed-asset-banner-label-font-weight);
  text-transform: uppercase;
  color: var(--p-discussed-asset-banner-label-color);
}

.discussed-asset-banner__offer {
  white-space: nowrap;
  font-size: var(--p-discussed-asset-banner-offer-font-size);
  font-weight: var(--p-discussed-asset-banner-offer-font-weight);
}

.discussed-asset-banner__offer--info {
  color: var(--p-discussed-asset-banner-offer-info-color);
}

.discussed-asset-banner__offer--warning {
  color: var(--p-discussed-asset-banner-offer-warning-color);
}

.discussed-asset-banner__offer--success {
  color: var(--p-discussed-asset-banner-offer-success-color);
}

.discussed-asset-banner__offer--danger {
  color: var(--p-discussed-asset-banner-offer-danger-color);
}

.discussed-asset-banner__offer--neutral {
  color: var(--p-discussed-asset-banner-offer-neutral-color);
}

.discussed-asset-banner__offer-amount {
  font-family: var(--p-discussed-asset-banner-offer-amount-font-family);
  font-variant-numeric: tabular-nums;
}

/* ── The asset ─────────────────────────────────────────────────────── */
.discussed-asset-banner__row {
  display: flex;
  align-items: center;
  gap: var(--p-discussed-asset-banner-row-gap);
  padding: var(--p-discussed-asset-banner-row-padding);
}

.discussed-asset-banner__media {
  position: relative;
  flex-shrink: 0;
  width: 56px;
  height: 42px;
  border-radius: var(--p-discussed-asset-banner-media-border-radius);
  overflow: hidden;
  background: var(--p-discussed-asset-banner-media-background);
}

.discussed-asset-banner__media img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.discussed-asset-banner__media-fallback {
  position: absolute;
  inset: 0;
  background: var(--p-discussed-asset-banner-media-fallback-background);
}

.discussed-asset-banner__body {
  flex: 1;
  min-width: 0;
}

.discussed-asset-banner__title {
  font-size: var(--p-discussed-asset-banner-title-font-size);
  font-weight: var(--p-discussed-asset-banner-title-font-weight);
  color: var(--p-discussed-asset-banner-title-color);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.discussed-asset-banner__meta {
  margin-top: 2px;
  font-size: var(--p-discussed-asset-banner-meta-font-size);
  color: var(--p-discussed-asset-banner-meta-color);
  display: flex;
  gap: var(--p-discussed-asset-banner-meta-gap);
  align-items: baseline;
}

.discussed-asset-banner__dot {
  color: var(--p-discussed-asset-banner-dot-color);
}

.discussed-asset-banner__figures {
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--p-discussed-asset-banner-figures-font-size);
  color: var(--p-discussed-asset-banner-figures-color);
}

.discussed-asset-banner__figure {
  font-family: var(--p-discussed-asset-banner-figure-font-family);
  font-variant-numeric: tabular-nums;
  font-weight: var(--p-discussed-asset-banner-figure-font-weight);
  color: var(--p-discussed-asset-banner-figure-color);
}

.discussed-asset-banner__figure--mmr {
  color: var(--p-discussed-asset-banner-figure-mmr-color);
}

.discussed-asset-banner__view {
  background: var(--p-discussed-asset-banner-view-background);
  color: var(--p-discussed-asset-banner-view-color);
  border: none;
  font-family: inherit;
  font-weight: var(--p-discussed-asset-banner-view-font-weight);
  font-size: var(--p-discussed-asset-banner-view-font-size);
  cursor: pointer;
  padding: var(--p-discussed-asset-banner-view-padding);
}

/* ── View offer ────────────────────────────────────────────────────── */
/* 40px as drawn; the hit area reaches 4px up into the row, which holds no
   control, for 44. The card clips anything below its own edge. */
.discussed-asset-banner__footer {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--p-discussed-asset-banner-footer-gap);
  width: 100%;
  height: 40px;
  padding: 0;
  border: none;
  border-top: var(--p-discussed-asset-banner-footer-border-top-width) solid var(--p-discussed-asset-banner-footer-border-top-color);
  background: var(--p-discussed-asset-banner-footer-background);
  color: var(--p-discussed-asset-banner-footer-color);
  font-family: inherit;
  font-size: var(--p-discussed-asset-banner-footer-font-size);
  font-weight: var(--p-discussed-asset-banner-footer-font-weight);
  cursor: pointer;
}

.discussed-asset-banner__footer::after {
  content: '';
  position: absolute;
  inset: -4px 0 0;
}

.discussed-asset-banner__footer:hover,
.discussed-asset-banner__footer:active {
  background: var(--p-discussed-asset-banner-footer-hover-background);
}

/* Inside the edge, which the card's rounded clip would otherwise cut. */
.discussed-asset-banner__footer:focus-visible {
  outline: 2px solid var(--p-discussed-asset-banner-footer-focus-outline-color);
  outline-offset: -2px;
}

.discussed-asset-banner__footer-icon {
  font-size: var(--p-discussed-asset-banner-footer-icon-font-size);
}
</style>
